import React from 'react';
import { ArrowRight, CheckCircle, Award } from 'lucide-react';

const Hero = () => {
  return (
    <section className="relative min-h-screen flex items-center overflow-hidden">
      {/* Video Background */}
      <div className="absolute inset-0">
        {/* Mobile: fast-loading still image */}
        <img
          src="/videos/hero-poster.webp"
          alt="Organic terraced farm fields"
          className="absolute inset-0 w-full h-full object-cover md:hidden"
          data-testid="hero-background-poster"
        />
        {/* Desktop/tablet: video */}
        <video
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          poster="/videos/hero-poster.webp"
          data-testid="hero-background-video"
          className="absolute inset-0 w-full h-full object-cover hidden md:block"
        >
          {/* Natural organic farming video - aerial over green crop fields (self-hosted) */}
          <source src="/videos/hero-farm.mp4" type="video/mp4" />
        </video>
        {/* Light overlay for text readability - keeps video natural */}
        <div className="absolute inset-0 bg-black/35"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-20">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div>
            {/* NPOP Badge */}
            <div className="inline-flex items-center gap-3 px-5 py-2 bg-amber-500/30 backdrop-blur-md rounded-full border border-amber-400/50 mb-8 shadow-xl">
              <Award className="text-amber-300" size={22} />
              <span className="text-amber-100 font-bold text-sm tracking-wide">NPOP CERTIFIED • ORGANIC EXPORTER</span>
            </div>

            {/* Main Heading */}
            <h1 className="font-serif mb-6 leading-tight">
              <span className="block text-white font-light text-4xl sm:text-5xl drop-shadow-[0_4px_20px_rgba(0,0,0,0.9)]">Pure Organics</span>
              <span className="block text-amber-300 font-semibold text-3xl sm:text-4xl mt-2 drop-shadow-[0_4px_20px_rgba(0,0,0,0.9)]">from the Hills of North East India</span>
              <span className="block text-amber-300 font-semibold text-3xl sm:text-4xl mt-1 drop-shadow-[0_4px_20px_rgba(0,0,0,0.9)]">& the Lands of South India</span>
            </h1>

            {/* Description */}
            <p className="text-xl text-white/90 mb-8 leading-relaxed font-light drop-shadow-[0_2px_10px_rgba(0,0,0,0.9)]">
              Sustainably sourced from the lush hills of North East India and the fertile plantations of South India, 
              we deliver premium organic agricultural products that meet international quality standards.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 mb-12">
              <a
                href="#products"
                className="inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-amber-500 to-amber-600 text-white rounded-lg font-semibold text-lg shadow-2xl hover:shadow-amber-500/50 transform hover:scale-105 transition-all duration-300"
              >
                Discover Our Range
                <ArrowRight className="ml-2" size={20} />
              </a>
              <a
                href="#contact"
                className="inline-flex items-center justify-center px-8 py-4 bg-white/10 backdrop-blur-md border-2 border-white/50 text-white rounded-lg font-semibold text-lg hover:bg-white/20 transition-all duration-300"
              >
                Partner With Us
              </a>
            </div>

            {/* Trust Indicators */}
            <div className="grid grid-cols-3 gap-6 pt-8 border-t border-white/20">
              {[
                { label: 'Farm Sourced', icon: CheckCircle },
                { label: 'NPOP Certified', icon: Award },
                { label: 'Global Export', icon: CheckCircle }
              ].map((item, index) => (
                <div key={index} className="text-center">
                  <item.icon className="text-emerald-300 mx-auto mb-2 drop-shadow-lg" size={24} />
                  <p className="text-white/80 text-sm drop-shadow-lg">{item.label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Right - Product Cards */}
          <div className="grid grid-cols-2 gap-3 max-w-md md:ml-auto">
            {[
              { 
                name: 'Organic Turmeric',
                desc: 'Fresh, Finger, Flakes & Powder',
                gradient: 'from-yellow-600/30 to-orange-600/30',
                border: 'border-yellow-400/50'
              },
              { 
                name: 'Organic Ginger',
                desc: 'Fresh, Powder & Flakes',
                gradient: 'from-orange-600/30 to-amber-600/30',
                border: 'border-orange-400/50'
              },
              { 
                name: 'Organic Buckwheat',
                desc: 'Flour & Unhulled, Premium Quality',
                gradient: 'from-amber-700/30 to-yellow-700/30',
                border: 'border-amber-400/50'
              },
              { 
                name: 'Black Cardamom',
                desc: 'Aromatic Spice',
                gradient: 'from-green-700/30 to-emerald-700/30',
                border: 'border-green-400/50'
              }
            ].map((product, index) => (
              <div
                key={index}
                className={`relative bg-gradient-to-br ${product.gradient} backdrop-blur-md border ${product.border} rounded-xl p-4 hover:scale-105 transition-all duration-300 group shadow-xl`}
              >
                <div className="absolute top-2.5 right-2.5">
                  <CheckCircle className="text-emerald-300 drop-shadow-lg" size={16} />
                </div>
                <h3 className="text-white font-bold text-base mb-1 pr-5 drop-shadow-lg leading-snug">{product.name}</h3>
                <p className="text-white/80 text-xs drop-shadow-md">{product.desc}</p>
                <div className="mt-2.5 text-amber-300 text-[10px] font-bold tracking-wide drop-shadow-lg">NPOP CERTIFIED</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce">
        <div className="flex flex-col items-center text-white/60">
          <span className="text-sm mb-2">Scroll Down</span>
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
          </svg>
        </div>
      </div>
    </section>
  );
};

export default Hero;
